import { useEffect, type ReactNode } from 'react';

export type MobileSheetId =
  | 'libraries'
  | 'inspector'
  | 'toolMore'
  | 'shapes'
  | 'type';

interface MobileSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Fraction of viewport height (0–1). Default 0.72 */
  heightRatio?: number;
}

/** Bottom sheet host for phone layout (Libraries / Inspector / tool pickers). */
export default function MobileSheet({
  open,
  title,
  onClose,
  children,
  heightRatio = 0.72,
}: MobileSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ps-mobile-sheet-root" role="presentation">
      <button
        type="button"
        className="ps-mobile-sheet-backdrop"
        aria-label="Dismiss"
        onClick={onClose}
      />
      <div
        className="ps-mobile-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ height: `${Math.round(heightRatio * 100)}dvh` }}
      >
        <div className="ps-mobile-sheet-handle" aria-hidden />
        <header className="ps-mobile-sheet-header">
          <strong>{title}</strong>
          <button type="button" className="ps-mobile-sheet-close" onClick={onClose}>
            Done
          </button>
        </header>
        <div className="ps-mobile-sheet-body">{children}</div>
      </div>
    </div>
  );
}
